import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  DataState,
  Input,
  Modal,
  PageHeader,
  Table,
  useToast,
  type Column,
} from "@vms/ui-web";
import { useApi } from "../../auth/AuthProvider";
import { useQuery } from "../../api/queries";
import { useOrgContext } from "../../context/OrgContext";

interface AdminOrgRow {
  id: string;
  name: string;
  slug: string;
  billingEmail?: string | null;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export function Organisations() {
  const orgsQuery = useQuery<AdminOrgRow[]>("/api/admin/organisations");
  const api = useApi();
  const { addToast } = useToast();
  const { refetchOrgs } = useOrgContext();
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function toggleDisabled(org: AdminOrgRow) {
    const action = org.deletedAt ? "restore" : "disable";
    setBusyId(org.id);
    try {
      const res = await api(`/api/admin/organisations/${org.id}`, {
        method: "PATCH",
        body: JSON.stringify({ action }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? `Failed to ${action} org`, "error");
        return;
      }
      addToast(`${org.name} ${action === "disable" ? "disabled" : "restored"}`);
      orgsQuery.refetch();
      refetchOrgs();
    } finally {
      setBusyId(null);
    }
  }

  const rows = orgsQuery.data ?? [];

  const columns: Column<AdminOrgRow>[] = [
    {
      key: "name",
      header: "Name",
      render: (org) => (
        <Link to={`/org/${org.slug}`} className="font-medium text-gray-900 hover:underline">
          {org.name}
        </Link>
      ),
    },
    {
      key: "slug",
      header: "Slug",
      render: (org) => <code className="text-xs text-gray-500">{org.slug}</code>,
    },
    {
      key: "billingEmail",
      header: "Billing email",
      render: (org) => <span className="text-sm text-gray-700">{org.billingEmail || "—"}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (org) =>
        org.deletedAt ? (
          <Badge className="bg-red-100 text-red-700">Disabled</Badge>
        ) : (
          <Badge className="bg-green-100 text-green-700">Active</Badge>
        ),
    },
    {
      key: "createdAt",
      header: "Created",
      render: (org) => (
        <span className="text-sm text-gray-500">{new Date(org.createdAt).toLocaleDateString()}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (org) => (
        <div className="text-right">
          <Button
            variant={org.deletedAt ? "secondary" : "danger"}
            size="sm"
            loading={busyId === org.id}
            onClick={() => toggleDisabled(org)}
          >
            {org.deletedAt ? "Restore" : "Disable"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Organisations" subtitle="All organisations across the platform.">
        <Button onClick={() => setCreating(true)}>New organisation</Button>
      </PageHeader>

      <Card className="overflow-hidden">
        <DataState
          loading={orgsQuery.loading}
          error={orgsQuery.error}
          empty={!orgsQuery.loading && rows.length === 0}
          emptyMessage="No organisations yet."
        >
          <Table<AdminOrgRow>
            columns={columns}
            data={rows}
            keyExtractor={(o) => o.id}
          />
        </DataState>
      </Card>

      <NewOrgModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          orgsQuery.refetch();
          refetchOrgs();
        }}
      />
    </div>
  );
}

function NewOrgModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const api = useApi();
  const { addToast } = useToast();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [billingEmail, setBillingEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setBillingEmail("");
    setError(null);
  }

  function slugify(s: string): string {
    return s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  async function submit() {
    if (!name.trim() || !slug.trim()) {
      setError("Name and slug are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await api("/api/organisations", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          slug: slug.trim(),
          billingEmail: billingEmail.trim() || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.error ?? "Failed to create org");
        return;
      }
      addToast(`Created ${name}`);
      onCreated();
      reset();
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="New organisation"
    >
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!slugTouched) setSlug(slugify(e.target.value));
          }}
          placeholder="Richmond Motorsports"
          autoFocus
        />
        <Input
          label="Slug"
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          placeholder="richmond"
        />
        <Input
          label="Billing email"
          type="email"
          value={billingEmail}
          onChange={(e) => setBillingEmail(e.target.value)}
          placeholder="billing@example.com"
        />
        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button onClick={submit} loading={submitting}>
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}
